import * as React from 'react'

import { clsx } from 'clsx'

import { Text, Flex, Skeleton, Button, mergeRefs } from '@buffer-mono/popcorn'

import { getSlotIds } from '~publish/helpers/getSlotIds'

import { PostGridItemCard } from './PostGridItemCard'
import { PostListItemCard } from './PostListItemCard'
import { Sidepanel } from '../../../components/Sidepanel'
import { useCommentCountsForPost } from '../../context/CommentsByPostCountsContext'
import { postSupportsComments } from '../../../PostMiniature/utils'
import type { UsePostsListQueryResult } from '../hooks/usePostsListQuery'
import type { PostListItem, ViewMode } from '../types'
import { isGridViewSupported } from '../utils/gridViewSupport'

import cardStyles from './PostListItemCard.module.css'
import styles from './PostsList.module.css'

import type { JSX } from 'react'

function useScrollToSelected(
  selectedId: string | undefined,
  resetDep: unknown,
): React.RefObject<HTMLLIElement | null> {
  const selectedItemRef = React.useRef<HTMLLIElement | null>(null)
  const lastScrolledIdRef = React.useRef<string | null>(null)

  React.useLayoutEffect(() => {
    if (!selectedId || !selectedItemRef.current) return
    if (lastScrolledIdRef.current === selectedId) return
    lastScrolledIdRef.current = selectedId
    const el = selectedItemRef.current
    const prefersReducedMotion =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : true
    el.scrollIntoView({
      block: 'nearest',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    })
  }, [selectedId, resetDep])

  return selectedItemRef
}

type PostsListProps = {
  posts: PostListItem[]
  selectedPostId?: string
  onPostSelect: (postId: string) => void
  lastElementRef: UsePostsListQueryResult['lastElementRef']
  fetchingMore: boolean
  loading: boolean
  isMultiChannel: boolean
  viewMode?: ViewMode
  showOnlyPostsWithComments?: boolean
  onShowAllPosts?: () => void
  /** When true, the empty state uses the no-channels-connected copy. */
  noChannelsConnected?: boolean
}

export function PostsList({
  posts,
  selectedPostId,
  onPostSelect,
  lastElementRef,
  fetchingMore,
  loading,
  isMultiChannel,
  viewMode = 'list',
  showOnlyPostsWithComments = false,
  onShowAllPosts,
  noChannelsConnected = false,
}: PostsListProps): JSX.Element {
  const isInitialLoading = loading && posts.length === 0
  const hasNoPosts = !loading && posts.length === 0

  // When the channel(s) can't host comments, the posts query may still return
  // real posts (Publish supports the channel even though Comments doesn't),
  // but the design wants the empty list state regardless.
  if (noChannelsConnected) {
    return (
      <PostsListEmptyState
        isMultiChannel={isMultiChannel}
        showOnlyPostsWithComments={showOnlyPostsWithComments}
        onShowAllPosts={onShowAllPosts}
        noChannelsConnected
      />
    )
  }

  if (isInitialLoading) {
    return <PostsListSkeleton viewMode={viewMode} />
  }

  if (hasNoPosts) {
    return (
      <PostsListEmptyState
        isMultiChannel={isMultiChannel}
        showOnlyPostsWithComments={showOnlyPostsWithComments}
        onShowAllPosts={onShowAllPosts}
        noChannelsConnected={noChannelsConnected}
      />
    )
  }

  if (viewMode === 'grid') {
    return (
      <PostsGridView
        posts={posts}
        selectedPostId={selectedPostId}
        onPostSelect={onPostSelect}
        lastElementRef={lastElementRef}
        fetchingMore={fetchingMore}
      />
    )
  }

  return (
    <PostsListView
      posts={posts}
      selectedPostId={selectedPostId}
      onPostSelect={onPostSelect}
      lastElementRef={lastElementRef}
      fetchingMore={fetchingMore}
      isMultiChannel={isMultiChannel}
    />
  )
}

type PostsGridViewProps = {
  posts: PostListItem[]
  selectedPostId?: string
  onPostSelect: (postId: string) => void
  lastElementRef: UsePostsListQueryResult['lastElementRef']
  fetchingMore: boolean
}

function PostsGridView({
  posts,
  selectedPostId,
  onPostSelect,
  lastElementRef,
  fetchingMore,
}: PostsGridViewProps): JSX.Element {
  const gridViewPosts = React.useMemo(
    () =>
      posts.filter((post) => isGridViewSupported(post.resolved.channelService)),
    [posts],
  )
  const selectedItemRef = useScrollToSelected(selectedPostId, posts)

  return (
    <Sidepanel.List
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The selectable post grid is a listbox composite widget.
      role="listbox"
      aria-label="Select a post"
      className={styles.gridContainer}
    >
      {gridViewPosts.map((post, index) => {
        const isSelected = selectedPostId === post.resolved.id
        const isLastElement = index === gridViewPosts.length - 1
        const ref =
          isSelected || isLastElement
            ? mergeRefs(
                isSelected ? selectedItemRef : undefined,
                isLastElement ? lastElementRef : undefined,
              )
            : undefined

        return (
          <GridPostItem
            key={post.resolved.id}
            post={post}
            isSelected={isSelected}
            onSelect={onPostSelect}
            ref={ref}
          />
        )
      })}
      {fetchingMore && (
        <Sidepanel.ListItem className={styles.gridItem}>
          <Skeleton height="100%" width="100%" squared />
        </Sidepanel.ListItem>
      )}
    </Sidepanel.List>
  )
}

function isAllCommentsAddressed(
  counts: { unreplied: number; replied: number; dismissed: number } | undefined,
): boolean {
  if (counts == null) return false
  const total = counts.unreplied + counts.replied + counts.dismissed
  return total > 0 && counts.unreplied === 0
}

type GridPostItemProps = {
  post: PostListItem
  isSelected: boolean
  onSelect: (postId: string) => void
}

const GridPostItem = React.forwardRef<HTMLLIElement, GridPostItemProps>(
  function GridPostItem({ post, isSelected, onSelect }, ref) {
    const { counts } = useCommentCountsForPost(post.resolved.id)
    const allAddressed = isAllCommentsAddressed(counts)
    const supportsComments = postSupportsComments(post.resolved)

    const handleKeyDown = (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect(post.resolved.id)
      }
    }

    return (
      <Sidepanel.ListItem
        ref={ref}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Each keyboard-operable post is an option in the parent listbox.
        role="option"
        aria-selected={isSelected}
        selected={isSelected}
        className={styles.gridItem}
        tabIndex={0}
        onClick={() => onSelect(post.resolved.id)}
        onKeyDown={handleKeyDown}
      >
        <PostGridItemCard
          post={post.resolved}
          isAllAddressed={allAddressed}
          supportsComments={supportsComments}
        />
      </Sidepanel.ListItem>
    )
  },
)

type PostsListViewProps = {
  posts: PostListItem[]
  selectedPostId?: string
  onPostSelect: (postId: string) => void
  lastElementRef: UsePostsListQueryResult['lastElementRef']
  fetchingMore: boolean
  isMultiChannel: boolean
}

function PostsListView({
  posts,
  selectedPostId,
  onPostSelect,
  lastElementRef,
  fetchingMore,
  isMultiChannel,
}: PostsListViewProps): JSX.Element {
  const selectedItemRef = useScrollToSelected(selectedPostId, posts)

  return (
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The selectable post list is a listbox composite widget.
    <Sidepanel.List role="listbox" aria-label="Select a post">
      {posts.map((post, index) => {
        const isSelected = selectedPostId === post.resolved.id
        const isLastElement = index === posts.length - 1
        const ref =
          isSelected || isLastElement
            ? mergeRefs(
                isSelected ? selectedItemRef : undefined,
                isLastElement ? lastElementRef : undefined,
              )
            : undefined

        return (
          <ListPostItem
            key={post.resolved.id}
            post={post}
            isSelected={isSelected}
            isMultiChannel={isMultiChannel}
            onSelect={onPostSelect}
            ref={ref}
          />
        )
      })}
      {fetchingMore && <PostListItemSkeleton />}
    </Sidepanel.List>
  )
}

type ListPostItemProps = {
  post: PostListItem
  isSelected: boolean
  isMultiChannel: boolean
  onSelect: (postId: string) => void
}

const ListPostItem = React.forwardRef<HTMLLIElement, ListPostItemProps>(
  function ListPostItem({ post, isSelected, isMultiChannel, onSelect }, ref) {
    const { counts } = useCommentCountsForPost(post.resolved.id)
    const allAddressed = isAllCommentsAddressed(counts)
    const supportsComments = postSupportsComments(post.resolved)

    const handleKeyDown = (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect(post.resolved.id)
      }
    }

    return (
      <Sidepanel.ListItem
        ref={ref}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Each keyboard-operable post is an option in the parent listbox.
        role="option"
        aria-selected={isSelected}
        selected={isSelected}
        className={clsx(
          styles.postListItem,
          allAddressed && styles['post-list-item-addressed'],
        )}
        tabIndex={0}
        onClick={() => onSelect(post.resolved.id)}
        onKeyDown={handleKeyDown}
      >
        <PostListItemCard
          post={post.resolved}
          isMultiChannel={isMultiChannel}
          isAllAddressed={allAddressed}
          supportsComments={supportsComments}
        />
      </Sidepanel.ListItem>
    )
  },
)

function PostsListSkeleton({
  viewMode = 'list',
}: {
  viewMode?: ViewMode
}): JSX.Element {
  if (viewMode === 'grid') {
    return (
      <div className={styles.gridContainer}>
        {getSlotIds('post-grid-loading-skeleton', 6).map((skeletonId) => (
          <div key={skeletonId} className={styles.gridItem}>
            <Skeleton height="100%" width="100%" squared />
          </div>
        ))}
      </div>
    )
  }

  return (
    <Sidepanel.List aria-label="Loading posts">
      {getSlotIds('post-list-loading-skeleton', 5).map((skeletonId) => (
        <Sidepanel.ListItem key={skeletonId} className={styles.postListItem}>
          <Flex direction="column" gap="xs" className={cardStyles.postContent}>
            <Skeleton height="1.2rem" width="85%" rounded />
            <Skeleton height="0.875rem" width="60%" rounded />
          </Flex>
        </Sidepanel.ListItem>
      ))}
    </Sidepanel.List>
  )
}

function PostListItemSkeleton(): JSX.Element {
  return (
    <Sidepanel.ListItem className={styles.postListItem}>
      <Flex direction="column" gap="xs" className={cardStyles.postContent}>
        <Skeleton height="1.2rem" width="85%" rounded />
        <Skeleton height="0.875rem" width="60%" rounded />
      </Flex>
    </Sidepanel.ListItem>
  )
}

function PostsListEmptyState({
  isMultiChannel,
  showOnlyPostsWithComments = false,
  onShowAllPosts,
  noChannelsConnected = false,
}: {
  isMultiChannel: boolean
  showOnlyPostsWithComments?: boolean
  onShowAllPosts?: () => void
  noChannelsConnected?: boolean
}): JSX.Element {
  if (noChannelsConnected) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="md"
        className={styles.emptyState}
      >
        <Text size="sm" color="subtle" align="center">
          Your posts will appear here
        </Text>
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="md"
      className={styles.emptyState}
    >
      <Text size="lg" weight="medium" color="subtle">
        No posts found
      </Text>
      <Text size="sm" color="subtle" align="center">
        {showOnlyPostsWithComments
          ? 'When your posts receive comments, they\u2019ll appear here. Want to see all your posts?'
          : isMultiChannel
            ? 'There are no sent posts for your channels yet.'
            : 'There are no sent posts for this channel yet.'}
      </Text>
      {showOnlyPostsWithComments && onShowAllPosts && (
        <Button variant="secondary" size="small" onClick={onShowAllPosts}>
          Show All Posts
        </Button>
      )}
    </Flex>
  )
}
